import { defineStore } from 'pinia';
import _ from 'lodash';
import { ProductService } from '@/services/product';
import { IntentService } from '@/services/intents';
import i18n from '@/i18n';
import { useBotsStore } from '@/stores/bots';
import { useFormsStore } from '@/stores/forms';

/** An intent object as returned by the product API (loose shape). */
type Intent = Record<string, any>;

function getTranslation(intent: Intent, key: string, targetLanguage: string | false | null) {
  if (targetLanguage === false) {
    return intent[key];
  }

  // If there is a string translation for the language, use that
  if (typeof intent?.translation?.[targetLanguage as string]?.[key] === 'string') {
    return intent.translation[targetLanguage as string][key];
  }

  /*
  // only if intent is not in category 'own' or origin is not 'own'
  const categoryIsOwnOrSuggestion = Array.isArray(intent.categories) && intent.categories.filter((category) => category === 'own' || category === 'suggestion').length > 0;
  const originIsNotOwn = Array.isArray(intent.origin) && intent.origin.filter((origin) => origin !== 'own').length > 0;
  if (
    targetLanguage &&
    intent.translation &&
    (!categoryIsOwnOrSuggestion || originIsNotOwn)
  ) {
    if (intent?.translation[targetLanguage]?.[key] &&
      (!Array.isArray(intent.translation[targetLanguage][key]) || intent.translation[targetLanguage][key].length >= intent[key].length)) {
      return intent.translation[targetLanguage][key];
    }
  }
   */

  return intent[key];
}

interface IntentsState {
  intents: Intent[] | false; // all faq intents
  /** The intent currently opened in the editor; false when none */
  currentIntent: Intent | false;
  /** Currently loaded bot answer; false when none (legacy, unused here) */
  botAnswer: any | false;
  /** Currently loaded bot question; false when none (legacy, unused here) */
  botQuestion: any | false;
  /** All smalltalk intents of the current bot */
  smalltalks: Intent[];
  /** Suggested (not yet booked) intents */
  suggested: Intent[];
  /** Bot forms merged from getForms/getAllForms */
  forms: Intent[];
  /** Bot id the intents were fetched for; false before first fetch */
  currentBotId: string | false;
  /** Library of all known intents (own/standard/smalltalks/...) keyed by intent name */
  intentLibrary: Record<string, Intent>;
  /** True while intents are being fetched */
  intentsLoading: boolean;
  fallbackFaqIntent: Intent | string | null; // default FAQ Intent
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/intents.ts` (namespace `intents`).
 * Not persisted (no `intents` reducer entry in the legacy `vuexLocal`/`vuexSession` config in
 * `src/store/index.ts`) — in-memory only, no `vuexPersist` option.
 *
 * NOTE: the `getters` below are defined INLINE (not hoisted to a standalone `const`) on purpose —
 * Pinia's `defineStore` types the `this` inside each getter to the fully-resolved store (state +
 * evaluated getters + actions) via contextual `ThisType` inference, which only kicks in when the
 * getters object literal is passed directly as an argument. Hoisting it to an external `const`
 * loses that inference (a well-known Pinia/Vue generic-inference limitation), which matters here
 * because `displayName` and `isAutoTranslated` call sibling getters through `this`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - State-prop/getter identity-passthrough collisions (getter renamed `<name>Getter`, per
 *   design-doc rule): `intents` -> `intentsGetter`, `smalltalks` -> `smalltalksGetter`,
 *   `forms` -> `formsGetter`, `intentsLoading` -> `intentsLoadingGetter`,
 *   `suggested` -> `suggestedGetter`, `currentIntent` -> `currentIntentGetter`,
 *   `fallbackFaqIntent` -> `fallbackFaqIntentGetter`. In each case `useIntentsStore().<name>`
 *   (the raw state prop) returns the identical value — the renamed getter exists purely for
 *   parity with legacy `getters['intents/<name>']` call sites.
 * - Action `setCurrentIntent` (looked up an intent by name, then committed the mutation of the
 *   same name) + mutation `setCurrentIntent` (`state.currentIntent = current`) merged into ONE
 *   action `setCurrentIntent(name: string)`. IMPORTANT DIVERGENCE GUARD: legacy `clear()` called
 *   `commit('setCurrentIntent', false)` directly against the MUTATION (bypassing the action's
 *   name-lookup), unconditionally clearing `currentIntent`. The merged action can no longer do
 *   that (it expects an intent *name* and does a lookup), so `clear()` below assigns
 *   `this.currentIntent = false` directly instead of calling `this.setCurrentIntent(false)` — this
 *   preserves the exact legacy behavior rather than introducing a silent no-op regression.
 * - All other mutations had no same-named action/getter, so they become same-named actions
 *   unchanged: `setForms`, `setIntents`, `removeSuggested`, `removeIntent`, `addIntent`,
 *   `setIntent`, `setIntentState`, `setIntentRagActions`, `setIntentFollowupActions`,
 *   `setIntentFields`, `setSmalltalks`, `setSuggested`, `setIntentsLoading`, `clearLibrary`,
 *   `setLibrary`.
 * - Cross-namespace reads resolved via sibling stores (called inside the getter/action body, per
 *   design doc): `rootGetters['bots/currentBot']` -> `useBotsStore().currentBot`,
 *   `rootGetters['bots/getBot'](id)` -> `useBotsStore().getBot(id)`,
 *   `rootGetters['bots/getNLPTemplate']` -> `useBotsStore().getNLPTemplate`,
 *   `rootState.forms.forms` -> `useFormsStore().forms`.
 * - Dropped the unused legacy `import moment from 'moment'` (dead import, no usage found anywhere
 *   in the module).
 * - `description`'s pre-existing quirk (legacy comment: "this references the module-scope getters
 *   object, so `getters.fallbackFaqIntentEnabled` is the (always truthy) getter function itself")
 *   can't be reproduced by the same mechanism in Pinia (getters live in the store's own `this`,
 *   there's no separate module-scope `getters` object to shadow). Preserved the exact OBSERVABLE
 *   behavior instead: the condition is hardcoded `true` (the guarded block always ran in legacy
 *   too, unconditionally) — see the comment inline below.
 */
export const useIntentsStore = defineStore('intents', {
  state: (): IntentsState => ({
    intents: false, // all faq intents
    currentIntent: false,
    botAnswer: false,
    botQuestion: false,
    smalltalks: [],
    suggested: [],
    forms: [],
    currentBotId: false,
    intentLibrary: {},
    intentsLoading: false,
    fallbackFaqIntent: null, // default FAQ Intent
  }),
  getters: {
    /**
     * Renamed from legacy getter `intents` — collided with the `intents` state prop (Pinia keeps
     * getters and state in one registry, unlike Vuex; a same-named getter would shadow the state
     * prop and break direct-assignment actions like `setIntents`). This was a pure identity
     * passthrough, so `useIntentsStore().intents` (the raw state prop) returns the same value;
     * this getter is kept only for parity with legacy `getters['intents/intents']` call sites.
     */
    intentsGetter: (state: IntentsState) => {
      // return Object.keys(state.intentLibrary).map((key) => state.intentLibrary[key]);
      return state.intents;
    },
    /** Renamed from legacy getter `smalltalks` — identity passthrough colliding with the `smalltalks` state prop. */
    smalltalksGetter: (state: IntentsState) => {
      return state.smalltalks;
    },
    /** Renamed from legacy getter `forms` — identity passthrough colliding with the `forms` state prop. */
    formsGetter: (state: IntentsState) => {
      return state.forms;
    },
    /** Renamed from legacy getter `intentsLoading` — identity passthrough colliding with the `intentsLoading` state prop. */
    intentsLoadingGetter: (state: IntentsState) => {
      return state.intentsLoading;
    },
    // gibt ein intent mit dem identifier name zurück.
    intent: (state: IntentsState) => (name: string) => {
      if (
        !state.intents ||
        !Array.isArray(state.intents) ||
        !state.smalltalks ||
        !Array.isArray(state.smalltalks) ||
        !state.suggested ||
        !Array.isArray(state.suggested)
      ) {
        return null;
      }
      return state.intents
        .concat(state.suggested)
        .concat(state.smalltalks)
        .find((i) => {
          return i.intent === name;
        });
    },
    /**
     * Return all possible types of intent objects;
     * - topics
     * - smalltalks
     * - fallback FAQ
     * - forms
     * - suggested
     *
     * With the options to select only a subset of these.
     *
     * Default:
     * - topics
     * - smalltalks
     * - fallback FAQ
     * @param state
     * @returns {(include?: true | GetAllIntentsOptions = {topics: true, smalltalks: true, fallbackFaq: true}) => Object[]}
     */
    allIntents: (state: IntentsState) => (include: true | Record<string, any> = {topics: true, smalltalks: true, fallbackFaq: true}) => {
      const intents: Intent[] = [];

      if (include === true || include.topics) {
        intents.push(...(state.intents as Intent[]));
      }

      if (include === true || include.smalltalks) {
        intents.push(...state.smalltalks);
      }

      // The fallback FAQ is usually also a topic already, so don't list it twice
      const fallbackFaq = state.fallbackFaqIntent as Intent | null;
      if (
        (include === true || include.fallbackFaq) &&
        fallbackFaq && typeof fallbackFaq === 'object' &&
        !intents.some((i) => i.intent === fallbackFaq.intent)
      ) {
        intents.push(fallbackFaq);
      }

      if (include === true || include.forms) {
        intents.push(...state.forms);
      }

      if (include === true || include.suggested) {
        intents.push(...state.suggested);
      }

      return intents;
    },
    /** Renamed from legacy getter `suggested` — identity passthrough colliding with the `suggested` state prop. */
    suggestedGetter: (state: IntentsState) => {
      return state.suggested;
    },
    bookedIntents: (state: IntentsState) => {
      return state.intents
        ? state.intents.filter((i) => {
            return !i.suggested;
          })
        : [];
    },
    /** Renamed from legacy getter `currentIntent` — identity passthrough colliding with the `currentIntent` state prop. */
    currentIntentGetter: (state: IntentsState) => {
      return state.currentIntent;
    },
    fetchedBotId: (state: IntentsState) => {
      return state.currentBotId;
    },
    isStandardIntent: (state: IntentsState) => (name: string) => {
      if (state.intentLibrary[name]) {
        return state.intentLibrary[name].standard;
      }

      return false;
    },
    isOriginIntent: (state: IntentsState) => (name: string, category = 'common') => {
      if (state.intentLibrary[name]) {
        return state.intentLibrary[name].origin?.find((o: string) => o === category);
      }
      return false;
    },
    description(state: IntentsState) {
      return (name: string, language: string | false = false) => {
        // pre-existing quirk (see this module's top-level doc comment): legacy referenced the
        // module-scope `getters` object by closure, so the condition was always truthy (the
        // getter FUNCTION itself, never actually evaluated). Preserved verbatim as an
        // unconditional `true` — this branch always ran in legacy too.
        if (true) {
          if ((state.fallbackFaqIntent as Intent)?.intent && (state.fallbackFaqIntent as Intent).intent === name) {
            return i18n.global.t('knowledgeBase.defaultIntentDescription');
          }

          if (state.fallbackFaqIntent === name) {
            return i18n.global.t('knowledgeBase.defaultIntentDescription');
          }
        }


        if (state.intentLibrary[name]?.description) {
          return getTranslation(state.intentLibrary[name], 'description', language);
        }

        if ((state.intents as Intent[])?.length > 0) {
          const found = (state.intents as Intent[]).find((i) => i.intent === name);

          return found?.description || '';
        }

        if (state.smalltalks?.length > 0) {
          const found = (state.intents as Intent[]).find((i) => i.intent === name);

          return found?.description || '';
        }

        if (state.suggested?.length > 0) {
          const found = state.suggested.find((i) => i.intent === name);

          return found?.description || '';
        }

        return '';
      };
    },
    /**
     * Converted from legacy getter `displayName: (state, getters, rootState) => (name, language) =>
     * {...}`. Unlike `description` above, the legacy code here referenced the Vuex-injected
     * `getters` PARAMETER (a real, correctly-evaluated getter lookup) — not the module-scope
     * closure — so this uses `this.fallbackFaqIntentEnabled` (real cross-getter access via
     * Pinia's `this` binding; requires this to stay a non-arrow method). `rootState.forms.forms`
     * -> `useFormsStore().forms`.
     */
    displayName(state: IntentsState) {
      return (name: string, language: string | false = false) => {
        if (this.fallbackFaqIntentEnabled) {
          if ((state.fallbackFaqIntent as Intent)?.intent && (state.fallbackFaqIntent as Intent).intent === name) {
            return i18n.global.t('knowledgeBase.defaultIntent');
          }

          if (state.fallbackFaqIntent === name) {
            return i18n.global.t('knowledgeBase.defaultIntent');
          }
        }
        // use intent library for display name
        if (state.intentLibrary[name]?.displayName) {
          return getTranslation(state.intentLibrary[name], 'displayName', language);
        }

        if ((state.intents as Intent[])?.length > 0) {
          const found = (state.intents as Intent[]).find((i) => i.intent === name);

          if (found) {
            return found.displayName || found.intent;
          }
        }

        if (state.smalltalks?.length > 0) {
          const found = (state.intents as Intent[]).find((i) => i.intent === name);

          if (found) {
            return found.displayName || found.intent;
          }
        }

        if (state.suggested?.length > 0) {
          const found = state.suggested.find((i) => i.intent === name);

          if (found) {
            return found.displayName || found.intent;
          }
        }

        if (state.forms?.length > 0) {
          let found = state.forms.find((i) => i.name === name);
          if (!found) {
            // legacy `rootState.forms.forms.find(...)` assumed `forms` was already an array here
            // (untyped `rootState: any`); preserved that assumption via cast rather than adding a
            // new falsy-guard that legacy didn't have.
            found = (useFormsStore().forms as Intent[]).find((i: any) => i.box === name);
          }
          if (found) {
            return found.displayName || found.name;
          }
        }

        return name;
      };
    },
    samples: (state: IntentsState) => (name: string, language: string | false = false) => {
      if (state.intentLibrary[name] && Array.isArray(state.intentLibrary[name].samples)) {
        return getTranslation(state.intentLibrary[name], 'samples', language);
      }

      return [];
    },
    /**
     * Converted from legacy `(state, getters) => (name, language) => {...}` — `getters.description(...)`
     * -> `this.description(...)` (real cross-getter call via Pinia's `this` binding).
     */
    isAutoTranslated(state: IntentsState) {
      return (name: string, language: string | false = false) => {
        if (!state.intentLibrary[name]?.description) {
          return false;
        }
        const descriptionDe = this.description(name, 'de');
        const descriptionEn = this.description(name, 'en');
        const descriptionEs = this.description(name, 'es');

        // legacy passed `null` (not `false`) here to `getters.description`; the getter's inner
        // `getTranslation` only special-cases `=== false`, so a `null` falls through to the same
        // "return raw own value" branch as `false` — identical observable result. Cast to satisfy
        // the now-stricter `string | false` param without changing the value passed.
        const descriptionOriginial = this.description(name, null as unknown as string | false);

        if (language === 'en' && descriptionEn !== descriptionOriginial) {
          return true;
        }
        if (language === 'de' && descriptionDe !== descriptionOriginial) {
          return true;
        }
        if (language === 'es' && descriptionEs !== descriptionOriginial) {
          return true;
        }
        return false;
      };
    },
    getIntentLibraryMap: (state: IntentsState) => {
      return state.intentLibrary;
    },
    getIntentsFromLibrary: (state: IntentsState) => {
      return Object.entries(state.intentLibrary).map((value) => {
        return value[1];
      });
    },
    getIntentsFromLibraryAndExclude: (state: IntentsState) => (categories: string | string[], standard = true) => {
      const intents = Object.entries(state.intentLibrary)
        .map((value) => {
          return value[1];
        })
        .filter((intent) => {
          if (standard && !intent.standard) return false;
          if (Array.isArray(categories) && categories.length > 1) {
            if (!intent.categories) return true;
            return !intent.categories.find((category: string) => {
              return categories.includes(category);
            });
          } else {
            return (
              !intent.categories ||
              !intent.categories.find((cat: string) => {
                return cat === categories;
              })
            );
          }
        });

      intents.sort((a, b) => {
        const aDisplay = (a.displayName || a.intent).trim();
        const bDisplay = (b.displayName || b.intent).trim();

        return aDisplay.localeCompare(bDisplay);
      });
      return intents;
    },
    getIntentsFromLibraryByCategory: (state: IntentsState) => (category: string, standard = true) => {
      const intents = Object.entries(state.intentLibrary)
        .map((value) => {
          return value[1];
        })
        .filter((intent) => {
          if (standard && !intent.standard) return false;
          return (
            intent.categories &&
            intent.categories.find((cat: string) => {
              return cat === category;
            })
          );
        });

      intents.sort((a, b) => {
        const aDisplay = (a.displayName || a.intent).trim();
        const bDisplay = (b.displayName || b.intent).trim();

        return aDisplay.localeCompare(bDisplay);
      });

      return intents;
    },
    getIntentFromLibraryByName: (state: IntentsState) => (name: string) => {
      if (!state.intentLibrary[name]) {
        return {
          displayName: name,
          intent: name,
          description: '',
          categories: [],
        };
      }
      return state.intentLibrary[name];
    },
    /** Renamed from legacy getter `fallbackFaqIntent` — identity passthrough colliding with the `fallbackFaqIntent` state prop. */
    fallbackFaqIntentGetter: (state: IntentsState) => {
      return state.fallbackFaqIntent;
    },
    /**
     * Check if the default FAQ Intent is enabled
     * @returns {boolean}
     */
    fallbackFaqIntentEnabled: (state: IntentsState) => {
      if (!state.fallbackFaqIntent) return false;

      const faq = useBotsStore().currentBot?.fallbackFaqIntent;
      if (!faq) return false;
      if (typeof faq === 'string') return true;

      return faq.active;
    },
  },
  actions: {
    // add new topic
    async add({ botId, samples, displayName, description, suggested, forward }: any) {
      const result: any = await ProductService.addIntent(
        botId,
        samples,
        displayName,
        description,
        suggested,
        forward
      );

      if (!result.error && result.intent) {
        this.addIntent(result.intent);
      }
      return result;
    },
    // convert suggestion to real topic
    async addSuggestion({ botId, intent }: { botId: string; intent: string }) {
      const result: any = await ProductService.changeIntentSuggestion(botId, intent);
      if (!result.error) {
        await new Promise((resolve) => setTimeout(resolve, 500));
        const faqIntent = this.suggested.find((i: Intent) => {
          return i.intent === intent;
        });
        // legacy `getters.suggested.find(...)` result was used unchecked (untyped `getters: any`);
        // if not found this threw at runtime in legacy too — preserved via non-null assertions
        // rather than adding a new not-found guard.
        faqIntent!.suggested = false;

        this.addIntent(faqIntent!);
        // legacy `commit('removeSuggested', faqIntent)` passed the whole intent object; the
        // mutation only ever reads `.intent` off it, so this narrows to the param shape rather
        // than dropping the (harmless) extra properties legacy also passed.
        this.removeSuggested(faqIntent! as { intent: string });
        return true;
      } else {
        return false;
      }
    },
    // DEPRECATED use addIntentFromSource instead
    async addSmalltalkFromStandard({ botId, intent }: { botId: string; intent: string }) {
      // ProductService add smalltalk from template
      const result: any = await ProductService.addSmalltalk(botId, intent);
      if (result && result.intent) {
        this.addIntent(result.intent);
      }

      return false;
    },
    async addIntentFromSource({ botId, intent, sourceBot }: { botId: string; intent: string; sourceBot: string }) {
      const result: any = await ProductService.addIntentFromSource(botId, intent, sourceBot);
      if (result && result.intent) {
        this.addIntent(result.intent);

        return result.intent;
      }

      return false;
    },
    /** Resolves to whether the intent was actually deleted. */
    async deleteIntent({ botId, intent }: { botId: string; intent: string }): Promise<boolean> {
      const result: any = await ProductService.deleteIntent(botId, intent);
      if (result.status !== 'ok') return false;

      this.removeIntent({ intent });
      this.removeSuggested({ intent });
      return true;
    },
    async get(uniqueBotId: string) {
      this.setIntentsLoading(true);

      // Get bot
      const botsStore = useBotsStore();
      let bot = botsStore.getBot(uniqueBotId);
      const generalFaqCallbackName = bot?.fallbackFaqIntent && typeof bot.fallbackFaqIntent === 'string'
       ? bot.fallbackFaqIntent
       : bot?.fallbackFaqIntent?.intent

      let intents: any = await ProductService.getIntents(uniqueBotId);
      const formResult: any = await ProductService.getForms(uniqueBotId);
      const allForms: any = await ProductService.getAllForms(uniqueBotId);
      // In case we need to use that later:
      if (generalFaqCallbackName) {
        this.fallbackFaqIntent = intents.find((i: Intent) => i.intent === generalFaqCallbackName) || null;
         // Remove "General FAQ Topic" Intent from all intents
        //intents = intents.filter((i) => i.intent !== generalFaqCallbackName);
      } else {
        this.fallbackFaqIntent = null;
      }

      this.currentBotId = uniqueBotId;

      const onlyIntents: Intent[] = Array.isArray(intents)
        ? intents.filter((i) => {
            return !i.intent.startsWith('smalltalk_') && !i.suggested;
          })
        : [];

      onlyIntents.sort((a, b) => {
        if (a.displayName.toLowerCase() < b.displayName.toLowerCase()) {
          return -1;
        }

        if (a.displayName.toLowerCase() > b.displayName.toLowerCase()) {
          return 1;
        }
        return 0;
      });

      const suggestedIntents: Intent[] = Array.isArray(intents)
        ? intents.filter((i) => {
            return !i.intent.startsWith('smalltalk_') && i.suggested;
          })
        : [];

      suggestedIntents.sort((a, b) => {
        if (a.displayName.toLowerCase() < b.displayName.toLowerCase()) {
          return -1;
        }

        if (a.displayName.toLowerCase() > b.displayName.toLowerCase()) {
          return 1;
        }
        return 0;
      });

      const onlySmalltalks: Intent[] = Array.isArray(intents)
        ? intents.filter((i) => {
            return i.intent.startsWith('smalltalk_');
          })
        : [];

      // add to library
      this.setLibrary({ intents: onlyIntents.slice(), category: 'own' }); // add intents to library
      this.setLibrary({
        intents: suggestedIntents.slice(),
        category: 'suggestion',
      }); // add suggestions to library
      this.setLibrary({
        intents: onlySmalltalks.slice(),
        category: 'smalltalks',
      }); // add suggestions to library
      await this.getStandard({ botId: uniqueBotId }); // add standard intent to library

      this.setIntents(onlyIntents);
      this.setSmalltalks(onlySmalltalks);
      this.setSuggested(suggestedIntents);

      // set forms and merge results
      const forms: Intent[] = [];
      if (allForms?.forms) {
        allForms.forms.forEach((form: any) => {
          form.name = form.intent;
          forms.push(form);
        });
      }

      // TODO figure out why we need to do this
      if (formResult && formResult.status === 'ok' && formResult.forms) {
        formResult.forms.filter((f: any) => f.intent).forEach((form: any) => {
          if (!forms.find((f) => f.intent === form.intent)) {
            forms.push(form);
          }
        });
        this.setForms(forms);
      }

      this.setIntentsLoading(false);
      return true;
    },
    async updateIntent(name: string) {
      const intent = (this.intents as Intent[]).concat(this.suggested).find((intent: Intent) => {
        return intent.intent === name;
      });

      if (intent) {
        await ProductService.updateIntent(intent);
        return true;
      } else {
        return false;
      }
    },
    /**
     * Merged from legacy action `setCurrentIntent` (looked up the intent by name then committed
     * the mutation of the same name) + legacy mutation `setCurrentIntent`. See the collision note
     * in this store's top-level doc comment for the `clear()` divergence guard this required.
     */
    setCurrentIntent(intent: string) {
      const currentIntent = this.intents ? (this.intents as Intent[])?.find((i: Intent) => i.intent === intent) : null;

      if (currentIntent) {
        this.currentIntent = currentIntent;
        return;
      }

      const smalltalkIntent = this.smalltalks?.find((i: Intent) => i.intent === intent);
      if (smalltalkIntent) {
        this.currentIntent = smalltalkIntent;
        return;
      }

    },
    clear() {
      this.setIntents(false);
      // NOTE: legacy `clear` committed the MUTATION `setCurrentIntent` directly with `false`
      // (distinct registry from the action of the same name, which expects an intent *name* and
      // does a lookup). Calling the merged `this.setCurrentIntent(false)` here would try to look
      // up an intent named `false` and silently do nothing instead of clearing `currentIntent` —
      // so this assigns state directly, preserving the exact legacy mutation effect.
      this.currentIntent = false;
      this.setSmalltalks([]);
      this.setSuggested([]);
      this.setForms([]);
      this.clearLibrary();
    },
    // fetch standard and smalltalk sets
    async getStandard({ botId }: { botId: string }) {
      const botsStore = useBotsStore();

      const [standards, smalltalks, templates]: any[] = await Promise.all([
        IntentService.getDefaults('standard', botId),
        IntentService.getDefaults('smalltalks', botId),
        botsStore.getNLPTemplate
          ? IntentService.getDefaults('template', botId)
          : Promise.resolve(null)
      ]);


      if (
        standards &&
        Array.isArray(standards.intents)
      ) {
        this.setLibrary({
          intents: standards.intents.map((intent: Intent) => {
            intent.standard = true;
            return intent;
          }),
          category: 'common',
        });
      }

      if (
        smalltalks &&
        Array.isArray(smalltalks.intents)
      ) {
        this.setLibrary({
          intents: smalltalks.intents.map((intent: Intent) => {
            intent.standard = true;
            return intent;
          }),
          category: 'smalltalks',
        });
      }

      let nlpTemplate = botsStore.getNLPTemplate;
      if (nlpTemplate && templates && Array.isArray(templates.intents)) {

        this.setLibrary({
          intents: templates.intents.map((intent: Intent) => {
            intent.standard = true;
            return intent;
          }),
          category: nlpTemplate.botId,
        });
      }
    },
    async fetchIntent(intent: any) {
      const result: any = await ProductService.getIntentDetails(
        intent.botId,
        intent.template,
        intent.intent
      );


      if (!result.state?.channels) return;

      this.setIntentState({
        intent: intent,
        status: result.state.channels,
      });
    },
    async saveK2Names(payload: any) {
      console.log('saveK2Names', payload);
    },

    // --- Converted from legacy `mutations` (same-named actions; no collisions with the getters
    // above other than the already-documented `setCurrentIntent` merge) ---

    setForms(forms: Intent[]) {
      this.forms = forms;
    },
    setIntents(intents: Intent[] | false) {
      this.intents = intents;
    },
    removeSuggested(intent: { intent: string }) {
      const index = this.suggested.findIndex((i) => {
        return i.intent === intent.intent;
      });

      if (index === -1) return;

      this.suggested.splice(index, 1);
    },
    removeIntent(intent: { intent: string }) {
      const index = (this.intents as Intent[]).findIndex((i) => {
        return i.intent === intent.intent;
      });

      if (index === -1) return;

      (this.intents as Intent[]).splice(index, 1);
    },
    addIntent(intent: Intent) {
      if (!this.intents && !Array.isArray(this.intents)) {
        return;
      }
      if (intent.template === 'Smalltalks') {
        this.smalltalks.push(intent);
      } else {
        (this.intents as Intent[]).push(intent);
      }
      (this.intents as Intent[]).sort((a, b) => {
        if (a.displayName.toLowerCase() < b.displayName.toLowerCase()) {
          return -1;
        }

        if (a.displayName.toLowerCase() > b.displayName.toLowerCase()) {
          return 1;
        }

        return 0;
      });

      if (
        this.intentLibrary[intent.intent] &&
        this.intentLibrary[intent.intent].standard
      ) {
        this.intentLibrary[intent.intent].standard = false;
      }
    },
    setIntent(intent: Intent) {
      if (!intent || !intent.intent || !Array.isArray(this.intents)) {
        return;
      }

      const index = this.intents.findIndex((i) => {
        return i.intent === intent.intent;
      });

      if (index === -1) return;

      // Replace Vue.set with direct assignment for Vue 3
      this.intents.splice(index, 1, intent);
       const libraryIntent = this.intentLibrary[intent.intent];
       if (libraryIntent) {
         this.intentLibrary[intent.intent] = {
           ...libraryIntent,
           ...intent,
         };
       }
     },
     setIntentState({ intent, status }: { intent: Intent; status: any }) {
       const found = (this.intents as Intent[]).find((i) => i.intent === intent.intent);
       if (found) found.state = status;
       if (this.intentLibrary[intent.intent]) {
        this.intentLibrary[intent.intent].state = status;
       }
     },
    /**
     * Overwrite the Rag Actions for a given intent
     * @param {string} intent The intent technical name
     * @param {{live: Object.<string, RAGActionPayload[]>, staging: Object.<string, RAGActionPayload[]>}} ragActions The Object string key is the channel ID
     */
    setIntentRagActions({ intent, ragActions }: { intent: string; ragActions: any }) {
      const found = (this.intents as Intent[]).find((i) => i.intent === intent);
      if (!found) return;

      found.ragActions = ragActions;
    },
    /**
     * Overwrite the Followup action for a given intent
     * @param {string} intent The intent technical name
     * @param {{live: Object, staging: Object}} followup The Object string key is the channel ID
     */
    setIntentFollowupActions({ intent, followup }: { intent: string; followup: any }) {
      const found = (this.intents as Intent[]).find((i) => i.intent === intent);
      if (!found) return;

      found.followup = followup;
    },
    /**
     * Updates the fields of an existing intent in the state and synchronizes it with the intent library if present.
     * @param {Object} payload - The payload containing the intent identifier and fields to update.
     * @param {Object} payload.intent - The intent object to identify which intent to update.
     * @param {...any} payload.fields - All fields to update in the intent.
     * @returns {void}
     */
    setIntentFields({ intent, ...fields }: { intent: string; [field: string]: any }) {
      const found = (this.intents as Intent[]).find((i) => i.intent === intent);
      if (!found) return;

      for (const key of Object.keys(fields)) {
        (found as any)[key] = fields[key];
      }

      const libraryIntent = this.intentLibrary[intent];
      if (libraryIntent) {
        this.intentLibrary[intent] = _.cloneDeep(found);
      }
    },
    setSmalltalks(smalltalks: Intent[]) {
      this.smalltalks = smalltalks;
    },
    setSuggested(suggested: Intent[]) {
      this.suggested = suggested;
    },
    setIntentsLoading(loading: boolean) {
      this.intentsLoading = loading;
    },
    clearLibrary() {
      this.intentLibrary = {};
    },
    setLibrary({ intents, category }: { intents: Intent[]; category?: string }) {
      for (const intent of intents) {
        if (category) {
          if (!intent.categories) {
            intent.categories = [];
          }
          // Avoid duplicate category entries
          if (!intent.categories.includes(category)) {
            intent.categories.push(category);
          }
          intent.origin = intent.categories;
        }

        if (!this.intentLibrary[intent.intent]) {
          this.intentLibrary = {
            ...this.intentLibrary,
            [intent.intent]: intent,
          };
        } else {
          // Keep origin/categories info in library
          this.intentLibrary[intent.intent].origin = intent.categories;
        }
      }
    },
  },
});
